'use client';

import {useCallback, useState} from 'react';
import {Spacer} from '@repo/ui/spacer';
import type {Question, TextSubmission} from 'iq-questions/types';
import {checkTextSubmission} from 'iq-questions/api';
import {HtmlContent} from 'common/components/html_content';
import {EditorToolbar} from 'common/components/editor_toolbar';
import {EditorError} from 'common/components/editor_error';

interface LlmEditorProps {
  data: Question;
  viewUrl?: string;
}

export function NonCodingEditor({data, viewUrl}: LlmEditorProps) {
  const [value, setValue] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<TextSubmission | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(async () => {
    if (!value.trim()) return;
    setRunning(true);
    setResult(null);
    setError(null);
    try {
      const res = await checkTextSubmission({
        question_uuid: data.uuid,
        solution: value,
      });
      setResult(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setRunning(false);
    }
  }, [data.uuid, value]);

  return (
    <div className='flex flex-col gap-(--iq-gap-sm)'>
      {/* Textarea */}
      <div className='border border-iq-grey-light rounded overflow-hidden'>
        <textarea
          className='w-full p-3 text-sm text-iq-grey-dark resize-none focus:outline-none disabled:opacity-50 disabled:bg-gray-50'
          style={{height: '20vh'}}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder='Write your answer here…'
          disabled={running}
        />
      </div>
      <Spacer />

      {/* Submit toolbar */}
      <EditorToolbar
        onClick={handleSubmit}
        disabled={running || !value.trim()}
        label='Submit answer'
        runningLabel='Evaluating…'
        running={running}
        viewUrl={viewUrl}
        hint='Your answer will be reviewed by AI'
      />

      {/* Error */}
      <EditorError error={error} />

      {/* Result */}
      {result && <LlmResult result={result} />}
    </div>
  );
}

function LlmResult({result}: {result: TextSubmission}) {
  const score = result.score;
  const scoreColor =
    score >= 7
      ? 'text-green-700 bg-green-50 border-green-200'
      : score >= 5
        ? 'text-yellow-700 bg-yellow-50 border-yellow-200'
        : 'text-red-700 bg-red-50 border-red-200';

  return (
    <div className='flex flex-col gap-3 p-4 border border-iq-grey-light rounded-lg bg-gray-50 mt-5'>
      <span className={`self-start text-sm font-semibold px-2 py-0.5 rounded border ${scoreColor}`}>
        Score: {score}/10
      </span>
      <div className='text-sm text-iq-grey-dark'>
        <HtmlContent html={result.feedback} />
      </div>
    </div>
  );
}
